import { BUSINESS_TIMEZONE } from './constants.ts'
import type { TopupQuote } from './contracts/billing.ts'

export interface TopupPricingInput {
  usdAmount: number
  usdIdrRate: number
  platformFeePercent: number
}

/**
 * Reproduces the V1 top-up pricing chain exactly:
 *   idr  = usd * rate
 *   fee  = round(idr * feePercent / 100)
 *   total = idr + fee
 * The client shows this live while typing; the server recomputes it as the
 * authoritative amount before creating a QR. Both call this one function so the
 * two can never disagree.
 */
export function calculateTopupQuote({
  usdAmount,
  usdIdrRate,
  platformFeePercent,
}: TopupPricingInput): TopupQuote {
  const idrAmount = Math.round(usdAmount * usdIdrRate)
  const platformFeeIdr = Math.round((idrAmount * platformFeePercent) / 100)

  return {
    usdAmount,
    usdIdrRate,
    platformFeePercent,
    idrAmount,
    platformFeeIdr,
    totalIdr: idrAmount + platformFeeIdr,
  }
}

/** Spendable balance in LiteLLM terms: everything topped up, minus everything spent. */
export function calculateBalance(toppedUpUsd: number, spentUsd: number): number {
  return toppedUpUsd - spentUsd
}

export function usdToIdr(usdAmount: number, usdIdrRate: number): number {
  return Math.round(usdAmount * usdIdrRate)
}

const IDR_FORMATTER = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

export function formatIdr(amount: number): string {
  return IDR_FORMATTER.format(amount)
}

export function formatUsd(amount: number, fractionDigits = 2): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount)
}

/** Usage costs are often far below a cent, so they need more precision than money. */
export function formatUsdPrecise(amount: number): string {
  if (amount === 0) return '$0'
  if (Math.abs(amount) < 0.01) return `$${amount.toFixed(6)}`
  return formatUsd(amount)
}

export function formatDateTime(isoString: string): string {
  return new Intl.DateTimeFormat('id-ID', {
    timeZone: BUSINESS_TIMEZONE,
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(isoString))
}

export function formatDate(isoString: string): string {
  return new Intl.DateTimeFormat('id-ID', {
    timeZone: BUSINESS_TIMEZONE,
    dateStyle: 'medium',
  }).format(new Date(isoString))
}

export function formatDuration(milliseconds: number): string {
  if (milliseconds < 1000) return `${Math.round(milliseconds)}ms`
  return `${(milliseconds / 1000).toFixed(2)}s`
}
